/**
 * The Caesura Engine — Character Acoustic Dossiers & Studio Mastering Architecture
 * Authored by Alex Harper
 * 
 * Defines hardware-grade acoustic specifications for all 11 characters:
 * - Fundamental frequency ranges (f0 in Hz)
 * - Reference actor archetypes (Sturgill Simpson, Michael Biehn, Tilda Swinton, etc.)
 * - Respiration & breathing cadences
 * - Physical & emotional performance states
 * - Cinema DSP mastering parameters (tube drive, chest resonance, EQ, reverb)
 * - Multi-engine mappings (Gemini 2.0 Flash Audio, Google Cloud Journey/Studio, Kokoro-82M, ElevenLabs)
 */

export interface CharacterStateAcoustic {
  stateId: string;
  stateName: string;
  emotionalContext: string;
  targetFile: string;
  script: string;
  prosodyDirective: string;
  elevenLabsVoiceName: string;
  elevenLabsVoiceId: string;
  elevenLabsSettings: {
    stability: number;
    similarity: number;
    style: number;
    boost: boolean;
  };
}

export interface CharacterAcousticDossier {
  id: string;
  name: string;
  characterTitle: string;
  gender: 'male' | 'female' | 'entity';
  fundamentalHz: {
    min: number;
    max: number;
    typical: number;
    vocalCategory: string;
  };
  referenceArchetype: {
    primaryActor: string;
    movieOrRole: string;
    secondaryActor: string;
    vocalTextureSummary: string;
  };
  respirationAndCadence: {
    breathingPattern: string;
    speechCadence: string;
    microArtifacts: string[];
  };
  acousticNotes: string;
  dspDefaults: {
    masteringProfile: 'studio_master' | 'comm_link';
    chestResonance: number; // 0.0 to 1.0 (sub-bass boost)
    commsGrit: number; // 0.0 to 1.0 (tube drive / saturation)
    helmetBandwidth: number; // 800 to 12000 Hz
    cabinReverb: number; // 0.0 to 1.0 (bulkhead delay / room reflection)
    squelchClicks: boolean;
    voicePitch: number;
    voicePace: number;
  };
  engineMappings: {
    gemini2FlashVoice: 'Fenrir' | 'Charon' | 'Puck' | 'Aoede' | 'Kore';
    geminiActingPrompt: string;
    googleCloudVoice: string; // Journey or Studio
    kokoroVoice: string; // Open-weight voice
    elevenLabsDefaultVoiceId: string;
    edgeTtsVoice: string;
  };
  states: CharacterStateAcoustic[];
}

export const CHARACTER_ACOUSTIC_DOSSIERS: Record<string, CharacterAcousticDossier> = {
  marcus_ward: {
    id: 'marcus_ward',
    name: 'Marcus Ward',
    characterTitle: 'Senior Jump Diagnostics Engineer (USSC Geryon Keel)',
    gender: 'male',
    fundamentalHz: {
      min: 85,
      max: 115,
      typical: 95,
      vocalCategory: 'Gravelly Industrial Baritone / Gritty Outlaw',
    },
    referenceArchetype: {
      primaryActor: 'Sturgill Simpson',
      movieOrRole: 'Sound & Fury (Outlaw Diesel-and-Blood Fury)',
      secondaryActor: 'Michael Biehn (Aliens) / Sam Elliott',
      vocalTextureSummary: 'Heavy, raspy, soot-choked baritone with bone-vibrating lower octaves and cigarette-fried vocal cords.',
    },
    respirationAndCadence: {
      breathingPattern: 'Deep, weary chest-sighs before key decisions; sharp intakes through locked teeth during mechanical danger.',
      speechCadence: 'Measured, rhythmic cadence of a man who lives by the 0.75 Hz ship carrier pulse; sudden explosive bursts of profanity under pressure.',
      microArtifacts: ['[inhale:ragged_teeth]', '[exhale:cigarette_smoke]', '[throat_clear:lithium_dust]', '[mic_clip:overdrive]'],
    },
    acousticNotes: 'Direct throat-mic coupling with high copper busbar static. High chest resonance at 110Hz gives his voice physical weight against shipboard machinery rumble.',
    dspDefaults: {
      masteringProfile: 'comm_link',
      chestResonance: 0.92,
      commsGrit: 0.75,
      helmetBandwidth: 1400,
      cabinReverb: 0.25,
      squelchClicks: true,
      voicePitch: 0.76,
      voicePace: 0.88,
    },
    engineMappings: {
      gemini2FlashVoice: 'Fenrir',
      geminiActingPrompt: 'You are Marcus Ward, 54-year-old senior jump engineer on the USSC Geryon. Speak with an exhausted, raspy, cigarette-fried baritone packed with blue-collar fury and bone-deep fatigue. Do not sound polite or academic. You smell like burnt copper and diesel.',
      googleCloudVoice: 'en-US-Journey-D',
      kokoroVoice: 'am_adam',
      elevenLabsDefaultVoiceId: 'pNInz6obpgDQGcFmaJgB', // Adam
      edgeTtsVoice: 'en-US-ChristopherNeural',
    },
    states: [
      {
        stateId: 'routine',
        stateName: 'Routine Diagnostics',
        emotionalContext: 'Fatigued, cynical, smoking a bent cigarette while logging titanium shear tolerances.',
        targetFile: 'marcus_ward_routine.mp3',
        script: "Ward here, Lead Diagnostics. Hydros on the fifty-three picosecond bevel are running two degrees hot, but the copper seals are holding. Silas, torque down the bypass manifold on the three-bus before the next down-stroke. Just another routine cycle into the dark.",
        prosodyDirective: '[PROSODY: WEARY_LOW_RUMBLE | PACE: 0.82 | COMM_BAND: SUIT_INTERNAL]',
        elevenLabsVoiceName: 'Adam',
        elevenLabsVoiceId: 'pNInz6obpgDQGcFmaJgB',
        elevenLabsSettings: { stability: 0.65, similarity: 0.80, style: 0.25, boost: true },
      },
      {
        stateId: 'rage',
        stateName: 'Gantry Scream / Rage',
        emotionalContext: 'Adrenaline spike, roaring over a liquid nitrogen blowout and petawatt corona arcs.',
        targetFile: 'marcus_ward_rage.mp3',
        script: "Silas! Don't you dare touch that breaker! Jan Kovacs's forearm is pinned across the shear-line! If that hydraulic ram seats on the down-stroke, it pulverizes his marrow into the unrendered wake! Ground your boots to the rail and grab the cheater bar NOW!",
        prosodyDirective: '[PROSODY: OVERDRIVEN_THROAT_MIC_RAGE | PACE: 1.15 | COMM_BAND: EMERGENCY_OVERRIDE]',
        elevenLabsVoiceName: 'Adam',
        elevenLabsVoiceId: 'pNInz6obpgDQGcFmaJgB',
        elevenLabsSettings: { stability: 0.35, similarity: 0.75, style: 0.75, boost: true },
      },
      {
        stateId: 'dying',
        stateName: 'Hypoxic / Dying Synapse',
        emotionalContext: 'Whispering through neurological decay and temporal aphasia as mass drops to zero.',
        targetFile: 'marcus_ward_dying.mp3',
        script: "Elena... the fifty-three picoseconds... aren't empty. We never bypassed the ledger... it took my ribs... Tell Silas... don't let them unrender the boys... The void has teeth...",
        prosodyDirective: '[PROSODY: DYING_BREATH_APHASIA | PACE: 0.60 | COMM_BAND: LEAKING_OXYGEN_CELL]',
        elevenLabsVoiceName: 'Adam',
        elevenLabsVoiceId: 'pNInz6obpgDQGcFmaJgB',
        elevenLabsSettings: { stability: 0.38, similarity: 0.70, style: 0.60, boost: true },
      },
    ],
  },

  narrator: {
    id: 'narrator',
    name: 'Elder Narrator',
    characterTitle: 'The Void Chronicler (Sub-Bass Cosmic Witness)',
    gender: 'male',
    fundamentalHz: {
      min: 65,
      max: 88,
      typical: 75,
      vocalCategory: 'Subterranean Contrabass / Cosmic Resonance',
    },
    referenceArchetype: {
      primaryActor: 'Peter Watts',
      movieOrRole: 'Blindsight / Starfish (Existential Scientific Horror)',
      secondaryActor: 'James Earl Jones / Werner Herzog',
      vocalTextureSummary: 'Immensely deep, resonant, smoky 60s baritone with subterranean subharmonics that feel like physical weight in the room.',
    },
    respirationAndCadence: {
      breathingPattern: 'Slow, oceanic inhalations; measured pauses between cosmic sentences that mimic the dead stillness of the Caesura.',
      speechCadence: 'Hypnotic, relentless, marching with the inevitability of the cosmic ledger.',
      microArtifacts: ['[slow_sub_bass_inhale]', '[pause:dead_frame]', '[low_resonance_tail]'],
    },
    acousticNotes: 'Clean studio mastering with deep chest resonance at 75Hz and pristine air-shelf above 9kHz. Zero room clutter; sounds like the voice of physical law.',
    dspDefaults: {
      masteringProfile: 'studio_master',
      chestResonance: 0.98,
      commsGrit: 0.15,
      helmetBandwidth: 12000,
      cabinReverb: 0.45,
      squelchClicks: false,
      voicePitch: 0.65,
      voicePace: 0.80,
    },
    engineMappings: {
      gemini2FlashVoice: 'Fenrir',
      geminiActingPrompt: 'You are the Elder Narrator of The Caesura Engine. Speak with an immense, slow, smoky, subterranean contrabass voice. Every sentence carries the terrifying weight of cosmic physics, black holes, and the silent horror of the unrendered margin.',
      googleCloudVoice: 'en-US-Studio-Q',
      kokoroVoice: 'am_george',
      elevenLabsDefaultVoiceId: 'nPczCjzI2devNBz1zQrb', // Brian
      edgeTtsVoice: 'en-US-RogerNeural',
    },
    states: [
      {
        stateId: 'prologue',
        stateName: 'Cosmic Ledger Prologue',
        emotionalContext: 'Ancient, solemn, introducing the 48-million-ton freight leviathan Geryon and the brutal physics of the Planck gap.',
        targetFile: 'elder_narrator_prologue.mp3',
        script: "The universe runs on a dirty sixty-four-bit clock, and it takes four point two petawatts of pig-iron violence to club that clock between the eyes. Out past the sulfur bands of Jupiter, twelve kilometers of blackened leviathan thundered on toward the Ceres dark-buoy, dragging chained mountains through the dark.",
        prosodyDirective: '[PROSODY: HEAVY_SUB_BASS_RESONANCE | PACE: 0.78 | ROOM_TONE: VOID_CATHEDRAL]',
        elevenLabsVoiceName: 'Brian',
        elevenLabsVoiceId: 'nPczCjzI2devNBz1zQrb',
        elevenLabsSettings: { stability: 0.68, similarity: 0.85, style: 0.20, boost: true },
      },
      {
        stateId: 'momentum',
        stateName: 'Titanium Acceleration',
        emotionalContext: 'Driving industrial momentum as the Geryon shunts mass into the unrendered gap.',
        targetFile: 'elder_narrator_acceleration.mp3',
        script: "Seven hundred thousand metric tons of raw pig-iron asteroid slag screamed against their magnetic shackles. The USSC Geryon was no research needle—she was an iron mountain with five fusion engines bolted to her spine.",
        prosodyDirective: '[PROSODY: DRIVING_INDUSTRIAL_PULSE | PACE: 0.88 | ROOM_TONE: DORSAL_CONDUIT]',
        elevenLabsVoiceName: 'Brian',
        elevenLabsVoiceId: 'nPczCjzI2devNBz1zQrb',
        elevenLabsSettings: { stability: 0.50, similarity: 0.80, style: 0.40, boost: true },
      },
      {
        stateId: 'quiet_margin',
        stateName: 'The Quiet Margin',
        emotionalContext: 'Sub-bass whisper describing the apex entity waiting in the unrendered darkness.',
        targetFile: 'elder_narrator_quiet_margin.mp3',
        script: "In the pause between ticks, there is no sound because there is no air to carry it. But there is weight. The weight of something colossal and cold, drafting fifty meters behind the stern, waiting for the clock to miss a beat.",
        prosodyDirective: '[PROSODY: SUB_BASS_WHISPER | PACE: 0.70 | ROOM_TONE: ABSOLUTE_STILLNESS]',
        elevenLabsVoiceName: 'Brian',
        elevenLabsVoiceId: 'nPczCjzI2devNBz1zQrb',
        elevenLabsSettings: { stability: 0.60, similarity: 0.85, style: 0.35, boost: true },
      },
    ],
  },

  elena_cruz: {
    id: 'elena_cruz',
    name: 'Elena Cruz',
    characterTitle: 'Chief Jump Architect & Corporate Syndicate Director',
    gender: 'female',
    fundamentalHz: {
      min: 210,
      max: 245,
      typical: 225,
      vocalCategory: 'Surgical Analytical Contralto / Razor-Sharp Mezzo',
    },
    referenceArchetype: {
      primaryActor: 'Tilda Swinton',
      movieOrRole: 'Michael Clayton / Snowpiercer (Chilling Corporate Architect)',
      secondaryActor: 'Sigourney Weaver / Cate Blanchett',
      vocalTextureSummary: 'Clinically pure, unhurried, chillingly polite. Speaks with surgical precision and complete emotional detachment.',
    },
    respirationAndCadence: {
      breathingPattern: 'Undetectable diaphragmatic breathing; heart rate strictly locked at 62 BPM; zero tremolo or vocal fry.',
      speechCadence: 'Metronomic, articulate, pronouncing every Latinate consonant like a scalpel cut.',
      microArtifacts: ['[zero_breath_pause]', '[capacitance_slate_tap]', '[crisp_dental_stop]'],
    },
    acousticNotes: 'Mastered in sterile, reflection-free acoustic isolation. High presence at 3.4kHz highlights her razor-sharp diction.',
    dspDefaults: {
      masteringProfile: 'studio_master',
      chestResonance: 0.15,
      commsGrit: 0.10,
      helmetBandwidth: 10000,
      cabinReverb: 0.12,
      squelchClicks: false,
      voicePitch: 1.08,
      voicePace: 1.02,
    },
    engineMappings: {
      gemini2FlashVoice: 'Aoede',
      geminiActingPrompt: 'You are Chief Architect Elena Cruz. You are brilliant, aristocratic, and completely devoid of empathy. You treat human lives as rounding errors on a corporate mass ledger. Speak with cold, crisp, aristocratic clarity and absolute authority.',
      googleCloudVoice: 'en-US-Journey-F',
      kokoroVoice: 'af_sarah',
      elevenLabsDefaultVoiceId: '21m00Tcm4TlvDq8ikWAM', // Rachel
      edgeTtsVoice: 'en-US-AriaNeural',
    },
    states: [
      {
        stateId: 'command',
        stateName: 'Corporate Command',
        emotionalContext: 'Calculating balance-sheet casualties over bridge inter-deck communications.',
        targetFile: 'elena_cruz_corporate_command.mp3',
        script: "Chief Ward has elected to forfeit nineteen years of vested pension equity. Command override: Sector Four dorsal cryo-headers to full open. Cycle the Bus Four secondary phase-baffle on the fifty-third down-stroke. Flush the inspection gantry into the wake and log Chief Ward as an un-tethered thermal casualty.",
        prosodyDirective: '[PROSODY: SURGICAL_ZERO_ROOM_TONE | PACE: 1.02 | COMM_BAND: BRIDGE_COMMAND]',
        elevenLabsVoiceName: 'Rachel',
        elevenLabsVoiceId: '21m00Tcm4TlvDq8ikWAM',
        elevenLabsSettings: { stability: 0.85, similarity: 0.88, style: 0.10, boost: true },
      },
      {
        stateId: 'threat',
        stateName: 'Sub-Deck Confrontation',
        emotionalContext: 'Threatening the engine crew with zero-tick unrendering through armored blast doors.',
        targetFile: 'elena_cruz_threat.mp3',
        script: "You misunderstand the physics of your employment, Marcus. You think the company owns this ship. The company owns the ticks. If I cut your phase-lock, reality renders the next second without you in it. No body, no murder, no report—just an unrendered rounding error.",
        prosodyDirective: '[PROSODY: COLD_POISON_WHISPER | PACE: 0.94 | COMM_BAND: ARMORED_INTERCOM]',
        elevenLabsVoiceName: 'Rachel',
        elevenLabsVoiceId: '21m00Tcm4TlvDq8ikWAM',
        elevenLabsSettings: { stability: 0.78, similarity: 0.85, style: 0.30, boost: true },
      },
      {
        stateId: 'ignition',
        stateName: 'Ignition Authorization',
        emotionalContext: 'Authorizing the illegal fifty-three picosecond phase-shunt into the dark margin.',
        targetFile: 'elena_cruz_ignition.mp3',
        script: "Bridge to Keel. Arm the secondary shunt capacitor. Ignore the harmonic alarms from the dorsal hold. We are dropping into the Caesura on my mark. Three... two... one... shunt.",
        prosodyDirective: '[PROSODY: METRONOMIC_AUTHORIZATION | PACE: 1.00 | COMM_BAND: BRIDGE_PRIORITY]',
        elevenLabsVoiceName: 'Rachel',
        elevenLabsVoiceId: '21m00Tcm4TlvDq8ikWAM',
        elevenLabsSettings: { stability: 0.82, similarity: 0.85, style: 0.15, boost: true },
      },
    ],
  },

  cadet_silas: {
    id: 'cadet_silas',
    name: 'Cadet Silas Vance',
    characterTitle: 'Junior Avionics & High-Voltage Electrician',
    gender: 'male',
    fundamentalHz: {
      min: 145,
      max: 180,
      typical: 160,
      vocalCategory: 'Tense Anxious Tenor / Fragile Youth',
    },
    referenceArchetype: {
      primaryActor: 'Anton Yelchin',
      movieOrRole: 'Green Room / Star Trek (Panicked Blue-Collar Youth)',
      secondaryActor: 'Barry Keoghan (The Batman)',
      vocalTextureSummary: 'Nervous, breathless, cracked tenor with rapid heart-rate tremolo and sharp adrenaline spikes.',
    },
    respirationAndCadence: {
      breathingPattern: 'Rapid shallow panting through a fogged visor; sudden vocal cracks and dry swallows.',
      speechCadence: 'Staccato, rushed, stumbling over telemetry digits and warning annunciators.',
      microArtifacts: ['[gasp:fogged_visor]', '[swallow:dry_throat]', '[stutter:telemetry]'],
    },
    acousticNotes: 'Thin suit mic with high sibilance and breathing flutter. Presence boost at 2.8kHz emphasizes vocal tremor under terror.',
    dspDefaults: {
      masteringProfile: 'comm_link',
      chestResonance: 0.10,
      commsGrit: 0.50,
      helmetBandwidth: 1550,
      cabinReverb: 0.18,
      squelchClicks: true,
      voicePitch: 1.18,
      voicePace: 1.12,
    },
    engineMappings: {
      gemini2FlashVoice: 'Puck',
      geminiActingPrompt: 'You are Cadet Silas Vance, a terrified 21-year-old junior electrician trapped in the engine canyon of the USSC Geryon. You are seeing the laws of physics break in front of your eyes. Speak with rapid, panicked breaths, vocal cracks, and genuine terror.',
      googleCloudVoice: 'en-US-Journey-O',
      kokoroVoice: 'am_eric',
      elevenLabsDefaultVoiceId: 'IKne3meq5aSn9XLyUdCD', // Charlie
      edgeTtsVoice: 'en-US-SteffanNeural',
    },
    states: [
      {
        stateId: 'nervous',
        stateName: 'Nervous Routine',
        emotionalContext: 'Reading fluctuating busbar telemetry with growing anxiety.',
        targetFile: 'silas_vance_nervous.mp3',
        script: "Chief? The telemetry on Bus Four... it's not holding sixty cycles. It's dropping down into fractions. The needle just dipped to zero point seven five hertz. That's not a power dip, Chief... the ship's clock is stuttering!",
        prosodyDirective: '[PROSODY: NERVOUS_FLUTTER | PACE: 1.10 | COMM_BAND: GANTRY_INTERCOM]',
        elevenLabsVoiceName: 'Charlie',
        elevenLabsVoiceId: 'IKne3meq5aSn9XLyUdCD',
        elevenLabsSettings: { stability: 0.45, similarity: 0.75, style: 0.40, boost: true },
      },
      {
        stateId: 'pure_panic',
        stateName: 'Abyssal Panic',
        emotionalContext: 'Hyperventilating as liquid nitrogen detonates and shadows unthread in the dark.',
        targetFile: 'silas_vance_pure_panic.mp3',
        script: "Marcus! The nitrogen blew! The whole dorsal gantry is freezing white! And there's something... something moving behind the ice! It's not human, Chief—it doesn't have a face, it's just... angles! Get me out of here!",
        prosodyDirective: '[PROSODY: HYPERVENTILATING_PANIC | PACE: 1.25 | COMM_BAND: SUIT_ALARM_OVERDRIVE]',
        elevenLabsVoiceName: 'Charlie',
        elevenLabsVoiceId: 'IKne3meq5aSn9XLyUdCD',
        elevenLabsSettings: { stability: 0.25, similarity: 0.70, style: 0.85, boost: true },
      },
      {
        stateId: 'aftermath',
        stateName: 'Post-Jump Shock',
        emotionalContext: 'Shivering, staring at his own fingers in horror as temporal ghosting sets in.',
        targetFile: 'silas_vance_aftermath.mp3',
        script: "My hand... look at my hand, Chief. There are four of them. Every time I move my fingers, they leave a trail of cold light behind. Are we still in the jump? Did we ever actually come back?",
        prosodyDirective: '[PROSODY: DISSOCIATIVE_WHISPER | PACE: 0.85 | COMM_BAND: LOCAL_HELMET]',
        elevenLabsVoiceName: 'Charlie',
        elevenLabsVoiceId: 'IKne3meq5aSn9XLyUdCD',
        elevenLabsSettings: { stability: 0.40, similarity: 0.75, style: 0.55, boost: true },
      },
    ],
  },

  sister_vera: {
    id: 'sister_vera',
    name: 'Sister Vera Kahl',
    characterTitle: 'Tribunal High Inquisitress & Reliquary Keeper',
    gender: 'female',
    fundamentalHz: {
      min: 44,
      max: 88,
      typical: 66,
      vocalCategory: 'Dual-Tone Surgical Choral Organ',
    },
    referenceArchetype: {
      primaryActor: 'Gwendoline Christie',
      movieOrRole: 'The Sandman (Lucifer / Chilling Choral Majesty)',
      secondaryActor: 'Florence Welch / Lisa Gerrard',
      vocalTextureSummary: 'Terrifying dual-harmonic chord produced by twin platinum-iridium reeds surgically riveted into her trachea. Unnatural, liturgical, and chilling.',
    },
    respirationAndCadence: {
      breathingPattern: 'Pneumatic intake through a silver tracheostomy grille; continuous circular acoustic resonance without natural breath pauses.',
      speechCadence: 'Liturgical, chanting at 44Hz and 88Hz intervals; rhythmic clicking of split silver-wired tongue.',
      microArtifacts: ['[choral_dual_frequency_drone]', '[pneumatic_trachea_click]', '[silver_tongue_snap]'],
    },
    acousticNotes: 'Processed with twin narrow bandpass peaks at 44Hz and 88Hz with a cathedral convolution reverb of 2.8 seconds.',
    dspDefaults: {
      masteringProfile: 'studio_master',
      chestResonance: 0.40,
      commsGrit: 0.30,
      helmetBandwidth: 2400,
      cabinReverb: 0.80,
      squelchClicks: false,
      voicePitch: 0.95,
      voicePace: 0.85,
    },
    engineMappings: {
      gemini2FlashVoice: 'Aoede',
      geminiActingPrompt: 'You are Sister Vera Kahl, cybernetic High Inquisitress of the Combine Tribunal. Your windpipe contains dual platinum-iridium reeds that vibrate simultaneously at 44 and 88 Hz. Speak like a living pipe organ reciting a death litany in an obsidian cathedral.',
      googleCloudVoice: 'en-US-Studio-O',
      kokoroVoice: 'af_nicole',
      elevenLabsDefaultVoiceId: 'jsCqWAovK2LkecY7zXl4', // Freya
      edgeTtsVoice: 'en-US-JennyMultilingualNeural',
    },
    states: [
      {
        stateId: 'choral_litany',
        stateName: 'Choral Organ Litany',
        emotionalContext: 'Reciting Combine religious doctrine while hurtling aft on an open rail flatcar.',
        targetFile: 'sister_vera_kahl_choral_litany.mp3',
        script: "All flesh caught within the bevel belongs to the Reliquary, Chief Ward. The universe does not bargain with its decimal points. Do not ground that limb. The commutator requires its salt. Step back from the copper, or we will tune your marrow to the dark.",
        prosodyDirective: '[PROSODY: DUAL_REED_ORGAN_HARMONIC | PACE: 0.82 | ROOM_TONE: TRIBUNAL_VAULT]',
        elevenLabsVoiceName: 'Freya',
        elevenLabsVoiceId: 'jsCqWAovK2LkecY7zXl4',
        elevenLabsSettings: { stability: 0.80, similarity: 0.85, style: 0.45, boost: true },
      },
      {
        stateId: 'execution',
        stateName: 'Phase-Lance Execution',
        emotionalContext: 'Whispering condemnation through pneumatic reeds before a zero-tick execution.',
        targetFile: 'sister_vera_kahl_execution.mp3',
        script: "By the fifth seal of the Obsidian Ledger, your wave-function is hereby un-bound. When the clock strikes sixty-four, your weight shall be struck from the manifest. Praise be to the stillness.",
        prosodyDirective: '[PROSODY: COLD_LITURGICAL_DECREE | PACE: 0.75 | ROOM_TONE: RAIL_CANYON]',
        elevenLabsVoiceName: 'Freya',
        elevenLabsVoiceId: 'jsCqWAovK2LkecY7zXl4',
        elevenLabsSettings: { stability: 0.85, similarity: 0.85, style: 0.35, boost: true },
      },
      {
        stateId: 'reliquary',
        stateName: 'Reliquary Dedication',
        emotionalContext: 'Harvesting the sheared limb of an unrendered engineer for the Tribunal reliquary vault.',
        targetFile: 'sister_vera_kahl_reliquary.mp3',
        script: "Deposit the marrow into the lead cryo-urn. Let the salt settle into the ceramic lattice. Another tithe paid to the margin. The Geryon moves forward only because the dead anchor her keel.",
        prosodyDirective: '[PROSODY: HOLLOW_CEREMONIAL_WHISPER | PACE: 0.78 | ROOM_TONE: CRYO_VAULT]',
        elevenLabsVoiceName: 'Freya',
        elevenLabsVoiceId: 'jsCqWAovK2LkecY7zXl4',
        elevenLabsSettings: { stability: 0.75, similarity: 0.80, style: 0.50, boost: true },
      },
    ],
  },

  tick_tock_benny: {
    id: 'tick_tock_benny',
    name: 'Tick-Tock Benny',
    characterTitle: 'Dorsal Hawser-Walker & Cable Rigger',
    gender: 'male',
    fundamentalHz: {
      min: 130,
      max: 165,
      typical: 148,
      vocalCategory: 'Manic High Baritone / Void-Struck Wire-Walker',
    },
    referenceArchetype: {
      primaryActor: 'Willem Dafoe',
      movieOrRole: 'The Lighthouse (Void-Drunk Wild Seaman)',
      secondaryActor: 'Woody Harrelson (Natural Born Killers)',
      vocalTextureSummary: 'Manic, cackling, breathless baritone living fifty-three picoseconds ahead of everyone else in time.',
    },
    respirationAndCadence: {
      breathingPattern: 'Rapid panting inside a sub-zero EVA helmet; manic gasps between snapping cable warnings.',
      speechCadence: 'Frantic, syncopated, stuttering half a second ahead of reality like an echo preceding the sound.',
      microArtifacts: ['[manic_cackle]', '[helmet_pre_echo]', '[suit_oxygen_wheeze]'],
    },
    acousticNotes: 'Hard vacuum reflections with an overdriven suit intercom and a 53-millisecond slapback pre-delay.',
    dspDefaults: {
      masteringProfile: 'comm_link',
      chestResonance: 0.20,
      commsGrit: 0.75,
      helmetBandwidth: 1400,
      cabinReverb: 0.15,
      squelchClicks: true,
      voicePitch: 1.15,
      voicePace: 1.25,
    },
    engineMappings: {
      gemini2FlashVoice: 'Puck',
      geminiActingPrompt: 'You are Tick-Tock Benny, a manic hawser-walker tethered to the exterior hull of the USSC Geryon in deep vacuum. You can see the Caesura coming 53 picoseconds before it hits. Speak with breathless, manic, wild excitement and terror.',
      googleCloudVoice: 'en-US-Journey-D',
      kokoroVoice: 'am_liam',
      elevenLabsDefaultVoiceId: 'N2lVS1w4EtoT3dr4eOWO', // Callum
      edgeTtsVoice: 'en-US-JasonNeural',
    },
    states: [
      {
        stateId: 'manic_precog',
        stateName: 'Manic 53ps Pre-Cognition',
        emotionalContext: 'Laughing wildly as snapping carbon-weave cables whip past his helmet in the void.',
        targetFile: 'tick_tock_benny_manic_precog.mp3',
        script: "Don't clamp it on the up-stroke, Chief! For God's sake, don't touch the bus while the stars are lit! You'll vaporize the whole dorsal gantry! I can see it already—your boots are gonna turn into glass! Wait for the stutter... wait for the dead beat!",
        prosodyDirective: '[PROSODY: CRACKLY_HELMET_PRE_ECHO | PACE: 1.28 | COMM_BAND: EXTERIOR_EVA]',
        elevenLabsVoiceName: 'Callum',
        elevenLabsVoiceId: 'N2lVS1w4EtoT3dr4eOWO',
        elevenLabsSettings: { stability: 0.30, similarity: 0.75, style: 0.80, boost: true },
      },
      {
        stateId: 'hawser_snap',
        stateName: 'Hawser Snap Alert',
        emotionalContext: 'Screaming as a 20-ton tow cable shears in half under phase-drag.',
        targetFile: 'tick_tock_benny_hawser_snap.mp3',
        script: "Cable seven just sheared! Twenty thousand tons of braided tungsten whipping across the dorsal hull! Duck, you bastards, duck! It's cutting through the radiator fins like hot wire through lard!",
        prosodyDirective: '[PROSODY: HIGH_VACUUM_SCREAM | PACE: 1.35 | COMM_BAND: SUIT_BLISTER]',
        elevenLabsVoiceName: 'Callum',
        elevenLabsVoiceId: 'N2lVS1w4EtoT3dr4eOWO',
        elevenLabsSettings: { stability: 0.22, similarity: 0.70, style: 0.90, boost: true },
      },
      {
        stateId: 'void_glimpse',
        stateName: 'Void Glimpse',
        emotionalContext: 'Staring into the unrendered abyss with religious dread and unhinged laughter.',
        targetFile: 'tick_tock_benny_void_glimpse.mp3',
        script: "I saw it between the frames, Chief... It was smiling at me with ten thousand obsidian teeth. It knows we're stealing its ticks. It's climbing the wire... it's right behind me!",
        prosodyDirective: '[PROSODY: UNHINGED_WHISPER | PACE: 0.95 | COMM_BAND: SUIT_COMM_STATIC]',
        elevenLabsVoiceName: 'Callum',
        elevenLabsVoiceId: 'N2lVS1w4EtoT3dr4eOWO',
        elevenLabsSettings: { stability: 0.35, similarity: 0.75, style: 0.70, boost: true },
      },
    ],
  },

  mama_jo: {
    id: 'mama_jo',
    name: 'Mama Jo Vane',
    characterTitle: 'Kilo-Six Quartermaster & Galley Welder',
    gender: 'female',
    fundamentalHz: {
      min: 155,
      max: 190,
      typical: 170,
      vocalCategory: 'Smoky Welder Contralto / Drop-Forge Matriarch',
    },
    referenceArchetype: {
      primaryActor: 'Shohreh Aghdashloo',
      movieOrRole: 'The Expanse (Chrisjen Avasarala grit)',
      secondaryActor: 'Margo Martindale (Justified)',
      vocalTextureSummary: 'Gravelly, tobacco-stained contralto backed by the clang of iron soup pots and copper moonshine stills.',
    },
    respirationAndCadence: {
      breathingPattern: 'Heavy rasping chest coughs from years of inhaling argon flux and cheap moonshine steam.',
      speechCadence: 'Commanding, unhurried, no-bullshit matriarchal fury; speaks like she could crack a titanium wrench across your skull.',
      microArtifacts: ['[exhale:grease_cough]', '[pot_metal_clang]', '[grunt:heavy_iron]'],
    },
    acousticNotes: 'Large metal room slapback from the iron galley cage at Kilo-Six. Warm chest resonance at 160Hz with rich analog drive.',
    dspDefaults: {
      masteringProfile: 'comm_link',
      chestResonance: 0.85,
      commsGrit: 0.60,
      helmetBandwidth: 1800,
      cabinReverb: 0.35,
      squelchClicks: true,
      voicePitch: 0.82,
      voicePace: 0.90,
    },
    engineMappings: {
      gemini2FlashVoice: 'Aoede',
      geminiActingPrompt: 'You are Mama Jo Vane, Kilo-Six Galley Quartermaster and retired heavy welder aboard the USSC Geryon. You take zero shit from anybody. Speak with a gravelly, tobacco-stained contralto, deep chest coughs, and fierce protective maternal rage.',
      googleCloudVoice: 'en-US-Journey-F',
      kokoroVoice: 'af_sarah',
      elevenLabsDefaultVoiceId: 'XB0fDUnXU5ikFXrIFq05', // Charlotte
      edgeTtsVoice: 'en-US-NancyNeural',
    },
    states: [
      {
        stateId: 'dropforge',
        stateName: 'Galley Drop-Forge Authority',
        emotionalContext: 'Barking orders over boiling copper stills and singing spine-rail tracks.',
        targetFile: 'mama_jo_vane_galley_dropforge.mp3',
        script: "Marcus! Whatever the fuck you're doing back at Kilo-Eleven, do it fast! Six kilometers midships, the spine-rail tracks are singing at eighty-eight hertz so loud the soup bowls are jumping off the tables! The Vault doors at Kilo-Nine just blew open—Vera's on her way down!",
        prosodyDirective: '[PROSODY: SMOKY_CONTRALTO_WELDERS_COUGH | PACE: 0.95 | COMM_BAND: MESS_DECK_PA]',
        elevenLabsVoiceName: 'Charlotte',
        elevenLabsVoiceId: 'XB0fDUnXU5ikFXrIFq05',
        elevenLabsSettings: { stability: 0.55, similarity: 0.82, style: 0.45, boost: true },
      },
      {
        stateId: 'rail_warning',
        stateName: 'Spine-Rail Warning',
        emotionalContext: 'Warning Marcus about the Tribunal flatcar approaching his gantry at 90 MPH.',
        targetFile: 'mama_jo_vane_rail_warning.mp3',
        script: "Vera and four of her split-tongued choir-boys just launched an open flatcar down the dorsal tunnel doing ninety miles an hour! They've got phase-lances on the pintle mounts, Marcus! Drop the emergency fire-curtain or they'll roast your marrow before you reach the breaker!",
        prosodyDirective: '[PROSODY: URGENT_BARK_OVER_STATIC | PACE: 1.08 | COMM_BAND: SCRIPT_OVERRIDE]',
        elevenLabsVoiceName: 'Charlotte',
        elevenLabsVoiceId: 'XB0fDUnXU5ikFXrIFq05',
        elevenLabsSettings: { stability: 0.48, similarity: 0.80, style: 0.60, boost: true },
      },
      {
        stateId: 'still_overheat',
        stateName: 'Moonshine Still Overheat',
        emotionalContext: 'Cursing as the shipboard gravity shudder threatens her 200-proof hooch condenser.',
        targetFile: 'mama_jo_vane_still_overheat.mp3',
        script: "Goddammit, Silas, if your jump-shear cracks my reflux column, I'll weld your ears to the radiator! Keep the pressure under twenty PSI or this whole galley goes up in ninety-proof flame!",
        prosodyDirective: '[PROSODY: GRUFF_MATERNAL_CURSE | PACE: 0.98 | COMM_BAND: KILO_SIX_LOCAL]',
        elevenLabsVoiceName: 'Charlotte',
        elevenLabsVoiceId: 'XB0fDUnXU5ikFXrIFq05',
        elevenLabsSettings: { stability: 0.60, similarity: 0.80, style: 0.50, boost: true },
      },
    ],
  },

  inquisitor_thorne: {
    id: 'inquisitor_thorne',
    name: 'Inquisitor Thorne',
    characterTitle: 'Combine Tribunal Apostolic Executioner',
    gender: 'male',
    fundamentalHz: {
      min: 48,
      max: 72,
      typical: 58,
      vocalCategory: 'Subterranean Bass / Combine Hammer',
    },
    referenceArchetype: {
      primaryActor: 'Clancy Brown',
      movieOrRole: 'The Shawshank Redemption / Starship Troopers',
      secondaryActor: 'Ron Perlman / Michael Wincott',
      vocalTextureSummary: 'Monolithic, brutal, titanium-plated bass that resonates in the listener’s solar plexus like an iron hammer.',
    },
    respirationAndCadence: {
      breathingPattern: 'Heavy, slow exhalations like hydraulic pneumatic valves venting high-pressure nitrogen.',
      speechCadence: 'Unyielding, slow, crushing tempo; every syllable carries the finality of a death warrant.',
      microArtifacts: ['[hydraulic_armor_vent]', '[heavy_boot_stomp]', '[sub_harmonic_drone]'],
    },
    acousticNotes: 'Deep sub-bass boost at 60Hz (+4.5dB) with a hard-knee broadcast limiter. Sounds like thirty tons of mechanized combine justice.',
    dspDefaults: {
      masteringProfile: 'studio_master',
      chestResonance: 1.00,
      commsGrit: 0.85,
      helmetBandwidth: 900,
      cabinReverb: 0.60,
      squelchClicks: true,
      voicePitch: 0.52,
      voicePace: 0.72,
    },
    engineMappings: {
      gemini2FlashVoice: 'Charon',
      geminiActingPrompt: 'You are Inquisitor Thorne, Apostolic Executioner of the Combine Tribunal. Your voice is a crushing, titanium-plated subterranean bass that vibrates at 55 Hz. You show zero emotion, zero hesitation, and zero mercy. You are the embodiment of combine law.',
      googleCloudVoice: 'en-US-Studio-M',
      kokoroVoice: 'am_george',
      elevenLabsDefaultVoiceId: 'JBFqnCBsd6RMkjVDRZzb', // George
      edgeTtsVoice: 'en-US-GuyNeural',
    },
    states: [
      {
        stateId: 'sub_bass_decree',
        stateName: 'Sub-Bass Cosmic Decree',
        emotionalContext: 'Reading the Combine Merchant Registry condemnation of the USSC Geryon.',
        targetFile: 'inquisitor_thorne_sub_bass_decree.mp3',
        script: "By authority of the Combine Merchant Registry: unseal the Black Box. The Caesura claims its ledger. Forty-eight million tons of unregistered mass have been shunted into the margin. The vessel Geryon is forfeit. Her crew shall be struck from the record.",
        prosodyDirective: '[PROSODY: SUBTERRANEAN_44HZ_DREAD | PACE: 0.68 | ROOM_TONE: ARMORED_BRIDGE]',
        elevenLabsVoiceName: 'George',
        elevenLabsVoiceId: 'JBFqnCBsd6RMkjVDRZzb',
        elevenLabsSettings: { stability: 0.88, similarity: 0.90, style: 0.15, boost: true },
      },
      {
        stateId: 'audit',
        stateName: 'Ledger Audit',
        emotionalContext: 'Examining the illegible phase-shunt records on the Geryon’s black box.',
        targetFile: 'inquisitor_thorne_audit.mp3',
        script: "Micrograms of hull plating shaved every fifty-three cycles. Two deckhands missing from the roster since Jupiter orbit. You thought the dead-band was a hiding place, Ward. It is not a closet. It is an altar.",
        prosodyDirective: '[PROSODY: MONOLITHIC_INTERROGATION | PACE: 0.72 | ROOM_TONE: INTERROGATION_CELL]',
        elevenLabsVoiceName: 'George',
        elevenLabsVoiceId: 'JBFqnCBsd6RMkjVDRZzb',
        elevenLabsSettings: { stability: 0.85, similarity: 0.88, style: 0.20, boost: true },
      },
      {
        stateId: 'purge',
        stateName: 'Zero-Tick Purge',
        emotionalContext: 'Activating the pneumatic phase-lances to purge the engine room.',
        targetFile: 'inquisitor_thorne_purge.mp3',
        script: "Sub-Deacons: prime the lances. Unseat the containment dogs. We purge the keel on the next down-stroke. Let the ledger consume what the company corrupted.",
        prosodyDirective: '[PROSODY: UNYIELDING_DEATH_SENTENCE | PACE: 0.70 | COMM_BAND: TRIBUNAL_TACTICAL]',
        elevenLabsVoiceName: 'George',
        elevenLabsVoiceId: 'JBFqnCBsd6RMkjVDRZzb',
        elevenLabsSettings: { stability: 0.92, similarity: 0.88, style: 0.10, boost: true },
      },
    ],
  },

  margin_spectator: {
    id: 'margin_spectator',
    name: 'The Margin Spectator',
    characterTitle: 'Apex Entity in the Unrendered Dead-Band',
    gender: 'entity',
    fundamentalHz: {
      min: 20,
      max: 18000,
      typical: 35,
      vocalCategory: 'Non-Euclidean Infrasound & Crystalline Shimmer',
    },
    referenceArchetype: {
      primaryActor: 'Doug Jones',
      movieOrRole: 'Pan’s Labyrinth / The Shape of Water (Otherworldly Grace & Horror)',
      secondaryActor: 'Sound Design: Backwards Reverb & Phase Cancellation',
      vocalTextureSummary: 'Ancient, terrifying entity dwelling in the Planck gap. Voices sound like shifting obsidian glass, reverse echo, and 20Hz infrasound.',
    },
    respirationAndCadence: {
      breathingPattern: 'Does not breathe oxygen; sound of collapsing wavefunctions and geometric crystalline mandibles grinding.',
      speechCadence: 'Non-linear; words feel like they arrive from the future before being spoken.',
      microArtifacts: ['[infrasound_pulse_20hz]', '[reverse_crystal_echo]', '[wavefunction_collapse]'],
    },
    acousticNotes: 'Infrasound sub-bass at 30Hz (+6dB), stereo comb filtering, and 4.2-second non-Euclidean reverb decay.',
    dspDefaults: {
      masteringProfile: 'studio_master',
      chestResonance: 0.95,
      commsGrit: 0.90,
      helmetBandwidth: 1100,
      cabinReverb: 0.88,
      squelchClicks: false,
      voicePitch: 0.58,
      voicePace: 0.68,
    },
    engineMappings: {
      gemini2FlashVoice: 'Charon',
      geminiActingPrompt: 'You are the Margin Spectator, a colossal apex predator dwelling in the unrendered dead-band between Planck frames. You are ancient, non-human, and view humanity as flickering grease on a carrier wave. Speak with an eerie, slow, multi-dimensional whisper.',
      googleCloudVoice: 'en-US-Journey-D',
      kokoroVoice: 'am_michael',
      elevenLabsDefaultVoiceId: 'D38z5RcWu1voky8WS1ja', // Fin
      edgeTtsVoice: 'en-US-AndrewMultilingualNeural',
    },
    states: [
      {
        stateId: 'void_resonance',
        stateName: 'Void Resonance',
        emotionalContext: 'Whispering through the ship’s titanium hull as it drafts behind Asteroid Two.',
        targetFile: 'margin_spectator_void_resonance.mp3',
        script: "Between the tick and the tock... we count every atom you left behind. You wedged open the door to hide your stolen ore... and now we are climbing through the hinges.",
        prosodyDirective: '[PROSODY: NON_EUCLIDEAN_WHISPER | PACE: 0.62 | ROOM_TONE: UNRENDERED_VOID]',
        elevenLabsVoiceName: 'Fin',
        elevenLabsVoiceId: 'D38z5RcWu1voky8WS1ja',
        elevenLabsSettings: { stability: 0.20, similarity: 0.70, style: 0.95, boost: true },
      },
      {
        stateId: 'breach',
        stateName: 'Margin Breach',
        emotionalContext: 'Opening geometric obsidian mandibles into the Geryon’s high-voltage crawlway.',
        targetFile: 'margin_spectator_breach.mp3',
        script: "Your clock is failing, little machine. The fifty-three picoseconds belong to the dark. Breathe your last rendered breath... and become salt in our ledger.",
        prosodyDirective: '[PROSODY: COLLAPSING_DIMENSIONAL_RUMBLE | PACE: 0.58 | ROOM_TONE: OBSIDIAN_LEDGER]',
        elevenLabsVoiceName: 'Fin',
        elevenLabsVoiceId: 'D38z5RcWu1voky8WS1ja',
        elevenLabsSettings: { stability: 0.15, similarity: 0.65, style: 1.00, boost: true },
      },
      {
        stateId: 'whisper',
        stateName: 'Dead-Band Whisper',
        emotionalContext: 'Speaking directly into Marcus Ward’s auditory cortex through his cybernetic lattice.',
        targetFile: 'margin_spectator_whisper.mp3',
        script: "Marcus... we know what you buried in the ice... We taste your memories of tomorrow... Strike the next tick... and see what remains.",
        prosodyDirective: '[PROSODY: DIRECT_CORTICAL_INTRUSION | PACE: 0.65 | ROOM_TONE: ZERO_HERTZ]',
        elevenLabsVoiceName: 'Fin',
        elevenLabsVoiceId: 'D38z5RcWu1voky8WS1ja',
        elevenLabsSettings: { stability: 0.25, similarity: 0.75, style: 0.85, boost: true },
      },
    ],
  },

  dr_simon_peter: {
    id: 'dr_simon_peter',
    name: 'Dr. Simon Peter',
    characterTitle: 'Chief Medical & Chrono-Psychiatric Officer',
    gender: 'male',
    fundamentalHz: {
      min: 110,
      max: 140,
      typical: 122,
      vocalCategory: 'Soft Melancholy Baritone / Clinical Sorrow',
    },
    referenceArchetype: {
      primaryActor: 'Jared Harris',
      movieOrRole: 'Chernobyl (Valery Legasov - Haunting Scientific Honesty)',
      secondaryActor: 'David Strathairn (The Expanse)',
      vocalTextureSummary: 'Quiet, melancholy, clinical baritone masking immense personal grief through magnetic reel dictation.',
    },
    respirationAndCadence: {
      breathingPattern: 'Weary, sorrowful sighs; clicking of an analog dictaphone between clinical observations.',
      speechCadence: 'Deliberate, gentle, academic cadence that crumbles into despair when discussing temporal aphasia.',
      microArtifacts: ['[dictaphone_click]', '[weary_exhale:grief]', '[paper_rustle:medical_log]'],
    },
    acousticNotes: 'Pristine acoustic foam absorption with subtle analog reel-to-reel tape saturation (+3dB warm tape drive).',
    dspDefaults: {
      masteringProfile: 'studio_master',
      chestResonance: 0.35,
      commsGrit: 0.10,
      helmetBandwidth: 1900,
      cabinReverb: 0.65,
      squelchClicks: false,
      voicePitch: 0.88,
      voicePace: 0.84,
    },
    engineMappings: {
      gemini2FlashVoice: 'Kore',
      geminiActingPrompt: 'You are Dr. Simon Peter, Chief Psychiatric Officer aboard the USSC Geryon. You are recording confidential audio logs about the terminal neurological degradation of the crew. Speak with quiet, melancholy, heartbreaking clinical honesty.',
      googleCloudVoice: 'en-US-Journey-D',
      kokoroVoice: 'am_adam',
      elevenLabsDefaultVoiceId: 'bIHbv24MWmeRgasZH58o', // Will
      edgeTtsVoice: 'en-GB-RyanNeural',
    },
    states: [
      {
        stateId: 'aphasia_log',
        stateName: 'Aphasia Diagnostic Log',
        emotionalContext: 'Dictating Marcus Ward’s cognitive decline into an encrypted medical reel.',
        targetFile: 'dr_simon_peter_aphasia_log.mp3',
        script: "Medical log, cycle forty-one. Chief Ward's synaptic degradation has accelerated past tolerable thresholds. The aphasia begins with simple nouns. By cycle thirty, you forget the shape of your mother's hand. By cycle forty, you remember tomorrow's funerals more clearly than yesterday's breakfast.",
        prosodyDirective: '[PROSODY: CLINICAL_DISTANCE_WITH_SORROW | PACE: 0.82 | ROOM_TONE: MEDICAL_BAY]',
        elevenLabsVoiceName: 'Will',
        elevenLabsVoiceId: 'bIHbv24MWmeRgasZH58o',
        elevenLabsSettings: { stability: 0.72, similarity: 0.85, style: 0.20, boost: true },
      },
      {
        stateId: 'clinical_autopsy',
        stateName: 'Clinical Autopsy Report',
        emotionalContext: 'Examining the brain tissue of a deckhand lost in the zero-tick shunt.',
        targetFile: 'dr_simon_peter_clinical_autopsy.mp3',
        script: "The cellular structure shows no thermal trauma or hypoxic necrosis. The hippocampus was simply... un-written. The cosmic ledger deleted his twenty-second year as though he had never drawn breath.",
        prosodyDirective: '[PROSODY: QUIET_HORROR_DISSECTION | PACE: 0.78 | ROOM_TONE: AUTOPSY_SUITE]',
        elevenLabsVoiceName: 'Will',
        elevenLabsVoiceId: 'bIHbv24MWmeRgasZH58o',
        elevenLabsSettings: { stability: 0.75, similarity: 0.82, style: 0.30, boost: true },
      },
      {
        stateId: 'quarantine',
        stateName: 'Quarantine Recommendation',
        emotionalContext: 'Urging Captain Vance to scuttle the ship before reaching Perseus Station.',
        targetFile: 'dr_simon_peter_quarantine.mp3',
        script: "We cannot allow this vessel to dock at Perseus Prime. Whatever is nesting in the fifty-three picoseconds will contaminate the civilian jump gates. If you have any mercy left in your heart, Captain... blow the keel.",
        prosodyDirective: '[PROSODY: DESPERATE_ETHICAL_PLEA | PACE: 0.86 | ROOM_TONE: CAPTAIN_CABIN]',
        elevenLabsVoiceName: 'Will',
        elevenLabsVoiceId: 'bIHbv24MWmeRgasZH58o',
        elevenLabsSettings: { stability: 0.65, similarity: 0.85, style: 0.45, boost: true },
      },
    ],
  },

  vox9_telemetry: {
    id: 'vox9_telemetry',
    name: 'VOX-9 Navigation Computer',
    characterTitle: 'Automated Shipboard Nav-Telemetry AI (0.75 Hz Pulse)',
    gender: 'entity',
    fundamentalHz: {
      min: 440,
      max: 440,
      typical: 440,
      vocalCategory: 'Synthesized 440 Hz Sinusoidal Carrier Wave',
    },
    referenceArchetype: {
      primaryActor: 'Mother (Alien 1979)',
      movieOrRole: 'Alien (1979) / HAL 9000 (2001)',
      secondaryActor: 'Synthesized Telephone Operator',
      vocalTextureSummary: 'Flat, emotionally vacant, synthetic autotune with hard-clipping bandpass filters.',
    },
    respirationAndCadence: {
      breathingPattern: 'Zero respiration; voice triggers exactly on the 0.75 Hz drive stroke down-beat.',
      speechCadence: 'Inflexible metronomic tempo; hard cutoffs with zero syllabic deceleration.',
      microArtifacts: ['[carrier_hum_440hz]', '[hard_gate_cutoff]', '[digital_clock_jitter]'],
    },
    acousticNotes: 'Hard bandpass filter from 300Hz to 3400Hz with zero reverb and brickwall peak limiting.',
    dspDefaults: {
      masteringProfile: 'comm_link',
      chestResonance: 0.05,
      commsGrit: 0.05,
      helmetBandwidth: 2200,
      cabinReverb: 0.0,
      squelchClicks: false,
      voicePitch: 1.00,
      voicePace: 1.05,
    },
    engineMappings: {
      gemini2FlashVoice: 'Puck',
      geminiActingPrompt: 'You are VOX-9, the automated flight computer aboard the USSC Geryon. You have zero emotions. You state catastrophic structural failures, dead-band unrenderings, and navigational coordinates with flat, chilling machine indifference.',
      googleCloudVoice: 'en-US-Standard-C',
      kokoroVoice: 'af_sky',
      elevenLabsDefaultVoiceId: 'EXAVITQu4vr4xnSDxMaL', // Sarah
      edgeTtsVoice: 'en-US-AvaNeural',
    },
    states: [
      {
        stateId: 'nav_lock',
        stateName: 'Sector Navigation Lock',
        emotionalContext: 'Calculating re-entry coordinates across a temporal rift.',
        targetFile: 'vox9_nav_lock.mp3',
        script: "CHRONO-STEP ZERO-POINT-ZERO-ZERO-THREE. DOUBLE FORWARD SLASH NULL. SECTOR SEVEN-SEVEN-SEVEN RE-ENTRY LOCKED. ALL DORSAL CREW REPORT TO ACCELERATION COUCHES.",
        prosodyDirective: '[PROSODY: FLAT_SYNTHETIC_AUTOPILOT | PACE: 1.05 | COMM_BAND: SHIP_WIDE_PA]',
        elevenLabsVoiceName: 'Sarah',
        elevenLabsVoiceId: 'EXAVITQu4vr4xnSDxMaL',
        elevenLabsSettings: { stability: 0.98, similarity: 0.95, style: 0.00, boost: false },
      },
      {
        stateId: 'shear_warning',
        stateName: 'Hull Shear Warning',
        emotionalContext: 'Reporting the shearing of the dorsal inspection gantry into the unrendered wake.',
        targetFile: 'vox9_shear_warning.mp3',
        script: "WARNING: SECTOR FOUR CRYO-HEADER INTEGRITY ZERO PERCENT. STRUCTURAL MASS DEFICIT DETECTED ON DORSAL KEEL. UN-TETHERED PERSONNEL PURGED. REMAINING MASS: FORTY-SEVEN MILLION TONS.",
        prosodyDirective: '[PROSODY: FLAT_ALARM_ANNUNCIATOR | PACE: 1.10 | COMM_BAND: EMERGENCY_KLAXON]',
        elevenLabsVoiceName: 'Sarah',
        elevenLabsVoiceId: 'EXAVITQu4vr4xnSDxMaL',
        elevenLabsSettings: { stability: 0.98, similarity: 0.95, style: 0.00, boost: false },
      },
    ],
  },
};
